/**
 * IndexedDB persistence for the TanStack Query cache.
 *
 * Canvas is slow, and this is where the "this feels good" payoff comes from:
 * on a second visit the app paints from a cache that survived the reload, then
 * revalidates behind it. Nothing here is authoritative — Canvas is the single
 * source of truth and any local cache is disposable (brainstorm.md 1.1) — so
 * serving briefly stale data costs nothing and a corrupt cache is thrown away
 * rather than repaired.
 *
 * The persister is hand-written over `idb` rather than pulled from
 * `@tanstack/query-async-storage-persister`, because it is about forty lines
 * and the alternative is another dependency plus a `localStorage` shim.
 * localStorage would be the wrong store anyway: it is synchronous, capped
 * around 5 MB, and a 204-assignment course serializes larger than people expect.
 */
import { openDB, type IDBPDatabase } from 'idb';
import {
  persistQueryClient,
  type PersistedClient,
  type Persister,
} from '@tanstack/react-query-persist-client';
import type { QueryClient } from '@tanstack/react-query';

const DB_NAME = 'cgc-cache';
const DB_VERSION = 1;
const STORE = 'query-cache';
const KEY = 'react-query';

/**
 * Bumped whenever the cached shape changes. A mismatch throws the whole cache
 * away, which is always safe here and much simpler than migrating it.
 */
const CACHE_BUSTER = 'v1';

/** How long a persisted cache stays usable before it is discarded on load. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

let dbPromise: Promise<IDBPDatabase> | null = null;

function db(): Promise<IDBPDatabase> {
  dbPromise ??= openDB(DB_NAME, DB_VERSION, {
    upgrade(database) {
      if (!database.objectStoreNames.contains(STORE)) database.createObjectStore(STORE);
    },
  });
  return dbPromise;
}

export function createIdbPersister(): Persister {
  return {
    async persistClient(client: PersistedClient) {
      try {
        await (await db()).put(STORE, client, KEY);
      } catch {
        // Storage can be unavailable or full. Losing the cache degrades
        // performance and nothing else, so this must never surface as an error.
      }
    },
    async restoreClient() {
      try {
        return (await (await db()).get(STORE, KEY)) as PersistedClient | undefined;
      } catch {
        return undefined;
      }
    },
    async removeClient() {
      try {
        await (await db()).delete(STORE, KEY);
      } catch {
        /* nothing to do */
      }
    },
  };
}

/**
 * Wire persistence onto a client. Returns an unsubscribe function.
 *
 * Mutations are deliberately NOT persisted here. A half-finished submission
 * resurrected from disk and replayed on next load is exactly the kind of
 * surprise that could double-submit homework. Failed writes are handled
 * explicitly by `writeQueue.ts` instead, where retry is a decision rather than
 * an accident (brainstorm.md 1.6).
 */
export function enableCachePersistence(queryClient: QueryClient): () => void {
  const [unsubscribe] = persistQueryClient({
    queryClient,
    persister: createIdbPersister(),
    maxAge: MAX_AGE_MS,
    buster: CACHE_BUSTER,
    dehydrateOptions: {
      shouldDehydrateMutation: () => false,
      shouldDehydrateQuery: (query) => {
        // Only persist successful data. Persisting an error would mean the app
        // opens showing yesterday's failure.
        if (query.state.status !== 'success') return false;
        // Never persist anything keyed on the token or proxy health — those
        // must be re-checked on every launch.
        const key = JSON.stringify(query.queryKey);
        return !key.includes('health');
      },
    },
  });

  return unsubscribe;
}

/** Drop the persisted cache from IndexedDB. */
export async function clearPersistedCache(): Promise<void> {
  await createIdbPersister().removeClient();
}

/**
 * Forget every trace of the current account's Canvas data.
 *
 * Call this whenever the token is cleared or replaced with a different one.
 *
 * Clearing the token alone is not enough and the gap is easy to miss: the token
 * lives in the `cgc` database and the query cache lives in `cgc-cache`, so
 * removing the credential leaves a full copy of the user's courses, teachers,
 * assignments and grades sitting on the device. The symptom is an app that says
 * "no token" while still showing the class list, and — more to the point —
 * personal data surviving what the user reasonably believed was a sign-out.
 *
 * Order matters. The in-memory cache goes first, because that is what empties
 * the screen without a reload; deleting the IndexedDB copy first would let the
 * persister write the still-populated in-memory cache straight back.
 */
export async function forgetCachedCanvasData(queryClient: QueryClient): Promise<void> {
  queryClient.clear();
  await clearPersistedCache();
}
